import Products from 'merchandise-lib/constants/Product';
import { buildFscCardStatus } from 'pricing-pages-ui-components/fsc-configure/fscCardStatus';
import { buildFscCreditPackSelection, buildFscCustomCreditConfig, buildFscPresetCreditPacks, fscMonthlyCostToUnits, fscSnapMonthlyCostToUnits, getFscCommittedCreditsPerUnit, getFscCommittedMaxUnits, getFscCommittedUnitPrice, getFscIncludedCredits, getFscPresetPackCommittedSkuQuantity, getFscSelectedPackId } from 'pricing-pages-ui-components/fsc-configure/fscCreditPacksHelpers';
import { getNordicsCustomerPricingUrl } from 'pricing-pages-ui-components/utils/publicDomainUtils';
import { useHistory, useLocation } from 'quartz-routing/react-router';
import { useCallback, useMemo } from 'react';
import { Screens } from '../../types/PublicNavItem';
import { TRACKING_SOURCES, trackAdditionalUnitsPicker, trackButtonClick } from '../../utils/tracking';
import { withFscCreditPackSelection } from './fscUrlCart';
const TRACKING_NAME = 'fsc-configure-credit-packs';
function trackCreditPacksClick(action) {
  trackButtonClick({
    action,
    name: TRACKING_NAME,
    trackingSource: TRACKING_SOURCES.FSC_CONFIGURE,
    screen: Screens.FSC_CONFIGURE
  });
}
function trackCreditStep({
  nextUnits,
  previousUnits,
  selectedTier,
  currencyCode
}) {
  if (nextUnits === previousUnits) return;
  trackAdditionalUnitsPicker({
    action: nextUnits > previousUnits ? 'stepper increase' : 'stepper decrease',
    displayedUnitQuantity: nextUnits,
    previousValue: previousUnits,
    apiName: Products.HUBSPOT_CREDITS_COMMITTED,
    productNames: [Products.HUBSPOT_CREDITS_COMMITTED],
    productLevel: selectedTier,
    activeCurrencyCode: currencyCode,
    trackingSource: TRACKING_SOURCES.FSC_CONFIGURE
  });
}
export function useFscCreditPacks({
  catalog,
  selectedTier,
  cart,
  currencyCode,
  term,
  productPlanOptions,
  isPriceLoading
}) {
  const history = useHistory();
  const {
    search
  } = useLocation();
  const selection = cart.creditPackSelection;
  const committedSkuQuantity = selection.kind === 'custom' ? selection.committedSkuQuantity : 0;
  const unitPrice = useMemo(() => {
    var _getFscCommittedUnitP;
    return (_getFscCommittedUnitP = getFscCommittedUnitPrice(productPlanOptions, term)) !== null && _getFscCommittedUnitP !== void 0 ? _getFscCommittedUnitP : 0;
  }, [productPlanOptions, term]);
  const presetPacks = useMemo(() => buildFscPresetCreditPacks(productPlanOptions, term), [productPlanOptions, term]);
  const maxUnits = getFscCommittedMaxUnits(catalog);
  const onSelectPack = useCallback(packId => {
    const next = buildFscCreditPackSelection(packId, getFscPresetPackCommittedSkuQuantity(productPlanOptions, packId));
    if (!next) return;
    trackCreditPacksClick(`selected ${packId}`);
    history.replace({
      search: withFscCreditPackSelection(search, next)
    });
  }, [history, productPlanOptions, search]);
  const onCustomUnitsChange = useCallback(units => {
    trackCreditStep({
      nextUnits: units,
      previousUnits: committedSkuQuantity,
      selectedTier,
      currencyCode
    });
    history.replace({
      search: withFscCreditPackSelection(search, {
        kind: 'custom',
        committedSkuQuantity: units
      })
    });
  }, [committedSkuQuantity, currencyCode, history, search, selectedTier]);
  const onCustomAmountStep = useCallback(monthlyCost => onCustomUnitsChange(fscMonthlyCostToUnits(monthlyCost, unitPrice, maxUnits)), [maxUnits, onCustomUnitsChange, unitPrice]);
  const onCustomAmountCommit = useCallback(monthlyCost => onCustomUnitsChange(fscSnapMonthlyCostToUnits(monthlyCost, unitPrice, maxUnits)), [maxUnits, onCustomUnitsChange, unitPrice]);
  const onToggleExpand = useCallback(expanded => trackCreditPacksClick(expanded ? 'expanded agent packs' : 'collapsed agent packs'), []);
  const onViewRateDetails = useCallback(() => trackCreditPacksClick('viewed rate details'), []);
  const data = useMemo(() => {
    if (productPlanOptions.length === 0) return null;
    const includedCredits = getFscIncludedCredits(catalog, selectedTier);
    return {
      includedCredits,
      currentCredits: includedCredits,
      additionalOwnedCredits: undefined,
      showCurrentLabel: false,
      currencyCode,
      selectedPackId: getFscSelectedPackId(selection),
      presetPacks,
      custom: buildFscCustomCreditConfig({
        unitPrice,
        creditsPerUnit: getFscCommittedCreditsPerUnit(catalog),
        maxUnits,
        committedSkuQuantity
      }),
      viewRateDetailsUrl: getNordicsCustomerPricingUrl(),
      onSelectPack,
      onCustomAmountStep,
      onCustomAmountCommit,
      onToggleExpand,
      onViewRateDetails
    };
  }, [catalog, committedSkuQuantity, currencyCode, maxUnits, onCustomAmountCommit, onCustomAmountStep, onSelectPack, onToggleExpand, onViewRateDetails, presetPacks, productPlanOptions, selectedTier, selection, unitPrice]);
  if (!data && !isPriceLoading) return null;
  return buildFscCardStatus({
    data,
    isPriceLoading
  });
}